/**
 * Vouchers —— 交易清單（`ui-spec.md` P-06）
 * ⭐ 按月自動分組（唔揀月份 = 全部，自動出月份小標題）＋ 篩選（公司／類別／付錢人／付款方式／搜尋）
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import TopBar from '../components/TopBar';
import { api, fmtMoney, type Meta, type VoucherFilters, type VoucherRow } from '../lib/api';

export default function Vouchers({ meta }: { meta: Meta }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<VoucherRow[]>([]);
  const [f, setF] = useState<VoucherFilters>({ entityId: meta.entities[0]?.id ?? '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setBusy(true);
    api.vouchers({ ...f, limit: 200 })
      .then((r) => setRows(r.vouchers))
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setBusy(false));
  }, [f]);

  /** 按月分組（唔靠 DB，純按 period 排好；已經是 DESC）*/
  const groups = useMemo(() => {
    const map = new Map<string, VoucherRow[]>();
    for (const r of rows) {
      const list = map.get(r.period) ?? [];
      list.push(r);
      map.set(r.period, list);
    }
    return [...map.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [rows]);

  const set = (patch: Partial<VoucherFilters>) => setF((prev) => ({ ...prev, ...patch }));

  return (
    <main className="page">
      <TopBar title={t('list.heading')} />

      <section className="card">
        <div className="fields">
          <label className="field">
            <span>{t('expense.entity')}</span>
            <select value={f.entityId ?? ''} onChange={(e) => set({ entityId: e.target.value })}>
              {meta.entities.map((e) => <option key={e.id} value={e.id}>{e.name_zh}</option>)}
            </select>
          </label>
          <label className="field">
            <span>{t('list.month')}</span>
            <input type="month" value={f.period ?? ''} onChange={(e) => set({ period: e.target.value })} />
          </label>
          <label className="field">
            <span>{t('list.kind')}</span>
            <select value={f.kind ?? ''} onChange={(e) => set({ kind: e.target.value })}>
              <option value="">{t('list.all')}</option>
              <option value="payment">{t('list.kind_payment')}</option>
              <option value="receipt">{t('list.kind_receipt')}</option>
            </select>
          </label>
          <label className="field">
            <span>{t('expense.paid_by')}</span>
            <select value={f.paidBy ?? ''} onChange={(e) => set({ paidBy: e.target.value })}>
              <option value="">{t('list.all')}</option>
              <option value="simon">{t('expense.paid_by_simon')}</option>
              <option value="raymond">{t('expense.paid_by_raymond')}</option>
              <option value="company">{t('expense.paid_by_company')}</option>
            </select>
          </label>
          <label className="field">
            <span>{t('expense.payment_method')}</span>
            <select value={f.paymentMethod ?? ''} onChange={(e) => set({ paymentMethod: e.target.value })}>
              <option value="">{t('list.all')}</option>
              {meta.paymentMethods.map((m) => <option key={m} value={m}>{t(`method.${m}`)}</option>)}
            </select>
          </label>
          <label className="field">
            <span>{t('list.search')}</span>
            <input value={f.q ?? ''} onChange={(e) => set({ q: e.target.value })} placeholder={t('list.search_hint')} />
          </label>
        </div>
        {busy && <p className="note">{t('list.loading')}</p>}
        {err && <p className="err">{err}</p>}
        <p className="note">{t('list.count', { n: rows.length })}</p>
      </section>

      {groups.length === 0 && !busy && <p className="note">{t('list.empty')}</p>}

      {groups.map(([period, list]) => {
        const total = list.reduce((s, r) => s + (r.kind === 'receipt' ? r.amount_cents : -r.amount_cents), 0);
        return (
          <section className="card" key={period}>
            <h2>{period}
              <span className={total >= 0 ? 'pos' : 'neg'}>　{t('list.net', { amount: fmtMoney(Math.abs(total)) })}</span>
            </h2>
            <ul className="list">
              {list.map((v) => (
                <li key={v.id}>
                  {v.attachment_id
                    ? <img className="thumb-xs" src={`/api/attachments/${v.attachment_id}`} alt="" />
                    : <span className="thumb-xs placeholder">📄</span>}
                  <span className="no">{v.voucher_no}</span>
                  <Link className="purpose" to={`/vouchers/${v.id}`}>
                    {v.purpose}
                    {v.vendor_name ? ` · ${v.vendor_name}` : ''}
                    {v.customer_name ? ` · ${v.customer_name}` : ''}
                  </Link>
                  <span className={v.kind === 'receipt' ? 'pos' : 'neg'}>
                    {v.kind === 'receipt' ? '+' : '−'}{fmtMoney(v.amount_cents)}
                  </span>
                  <span className="tag">{v.paid_by === 'company' ? t('expense.paid_by_company') : t(`expense.paid_by_${v.paid_by}`)}</span>
                  <span className="tag">{t(`method.${v.payment_method}`)}</span>
                  {v.reimbursement_status === 'pending' && <span className="tag warn">{t('list.pending_reimb')}</span>}
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </main>
  );
}
